import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  Button,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => boolean | void | Promise<boolean | void>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmAppearance?: 'primary' | 'secondary' | 'subtle';
  danger?: boolean;
  loading?: boolean;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmAppearance = 'primary',
  danger = false,
  loading = false,
}) => {
  const handleConfirm = async () => {
    const shouldClose = await onConfirm();
    if (shouldClose !== false) {
      onClose();
    }
  };

  // Retain the last non-empty title/message so content doesn't blank out
  // while the dialog plays its close animation (callers clear title/message
  // from the same state that toggles `open`).
  const [displayTitle, setDisplayTitle] = React.useState(title);
  const [displayMessage, setDisplayMessage] = React.useState(message);
  React.useEffect(() => {
    if (open) {
      setDisplayTitle(title);
      setDisplayMessage(message);
    }
  }, [open, title, message]);

  return (
    <Dialog open={open} onOpenChange={(_, data) => !loading && data.open === false && onClose()} data-testid="confirmation-dialog-root">
      <DialogSurface data-testid="confirmation-dialog">
        <DialogBody data-testid="confirmation-dialog-body">
          <DialogTitle
            data-testid="confirmation-dialog-title"
            action={
              <DialogTrigger action="close" disableButtonEnhancement>
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<Dismiss24Regular />}
                  disabled={loading}
                  data-testid="confirmation-dialog-close"
                />
              </DialogTrigger>
            }
          >
            {displayTitle}
          </DialogTitle>
          <DialogContent data-testid="confirmation-dialog-message">{displayMessage}</DialogContent>
          <DialogActions data-testid="confirmation-dialog-actions">
            <DialogTrigger disableButtonEnhancement data-testid="confirmation-dialog-trigger-cancel">
              <Button appearance="secondary" disabled={loading} data-testid="confirmation-dialog-cancel">
                {cancelText}
              </Button>
            </DialogTrigger>
            <Button
              appearance={danger ? 'primary' : confirmAppearance}
              onClick={handleConfirm}
              disabled={loading}
              style={danger ? { backgroundColor: '#d13438', borderColor: '#d13438' } : {}}
              data-testid="confirmation-dialog-confirm"
            >
              {loading ? 'Processing...' : confirmText}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};